import { NoDataWarning } from "@/components/common";
import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton } from "@/components/common";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { GetContract } from "@/services/fhir/contract/GetContract";
import { useFormatter } from "@/utils/useFormatter";
import {
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  Typography,
  Divider,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import { useEffect, useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

interface IPeriod {
  startDate: Date;
  endDate: Date;
  fluxmedAmount: number;
  count: number;
}

interface IPaymentNotice {
  resource: {
    paymentDate: string;
    amount: {
      value: number;
    };
  };
}

interface IExtensionInfo {
  name: string;
  startDate: string | null;
  isActive: boolean;
}

interface ICancelledExtensionInfo {
  name: string;
  startDate: string | null;
  endDate: string | null;
}

export function ExtensionsDetails({ isOpen, handleClose }: IProps) {
  const {
    contractFluxPay,
    paymentNoticeFluxPay,
    isLoadingPaymentNoticeFluxPay,
    fetchPaymentNoticeFluxPay,
  } = useFluxPayContractContext();
  const { contractDiscussionCase } = useDiscussionCaseContext();
  const { contractMedicalReports } = useMedicalReportsAgentContext();
  const { contractGuidance } = useGuidanceAgentContext();
  const { contractPostAttendance } = usePostAttendanceContext();
  const { organization } = useOrganizationContext();
  const hasFetchedRef = useRef(false);
  const navigate = useNavigate();
  const [cancelledExtensions, setCancelledExtensions] = useState<
    ICancelledExtensionInfo[]
  >([]);
  const [isLoadingCancelledExtensions, setIsLoadingCancelledExtensions] =
    useState(false);

  // Lista de todas as extensões disponíveis
  const extensionsList = useMemo(() => {
    const extensions: IExtensionInfo[] = [
      {
        name: "FluxPay",
        startDate: contractFluxPay?.term?.[0]?.applies?.start || null,
        isActive: !!contractFluxPay,
      },
      {
        name: "Agente de discussão de casos",
        startDate: contractDiscussionCase?.term?.[0]?.applies?.start || null,
        isActive: !!contractDiscussionCase,
      },
      {
        name: "Agente de relatórios médicos",
        startDate: contractMedicalReports?.term?.[0]?.applies?.start || null,
        isActive: !!contractMedicalReports,
      },
      {
        name: "Agente de orientações aos pacientes",
        startDate: contractGuidance?.term?.[0]?.applies?.start || null,
        isActive: !!contractGuidance,
      },
      {
        name: "Gestão de Pós-Atendimento",
        startDate: contractPostAttendance?.term?.[0]?.applies?.start || null,
        isActive: !!contractPostAttendance,
      },
    ];

    return extensions;
  }, [
    contractFluxPay,
    contractDiscussionCase,
    contractMedicalReports,
    contractGuidance,
    contractPostAttendance,
  ]);

  // Filtra apenas extensões ativas
  const activeExtensions = useMemo(() => {
    return extensionsList.filter((ext) => ext.isActive);
  }, [extensionsList]);

  // Busca contratos cancelados das extensões para exibir histórico
  useEffect(() => {
    async function fetchCancelledExtensions() {
      if (!organization?.id) {
        setCancelledExtensions([]);
        return;
      }

      try {
        setIsLoadingCancelledExtensions(true);

        const [
          cancelledDiscussionCase,
          cancelledMedicalReports,
          cancelledGuidance,
          cancelledPostAttendance,
        ] = await Promise.all([
          GetContract({
            organization: organization.id,
            count: 1,
            sort: true,
            status: "cancelled",
            identifier: "DISCUSSION_CASE_AGENT",
          }),
          GetContract({
            organization: organization.id,
            count: 1,
            sort: true,
            status: "cancelled",
            identifier: "MEDICAL_REPORTS_AGENT",
          }),
          GetContract({
            organization: organization.id,
            count: 1,
            sort: true,
            status: "cancelled",
            identifier: "GUIDANCE_AGENT",
          }),
          GetContract({
            organization: organization.id,
            count: 1,
            sort: true,
            status: "cancelled",
            identifier: "pos-atendimento",
          }),
        ]);

        const newCancelledExtensions: ICancelledExtensionInfo[] = [];

        const addCancelledExtension = (bundle: any, name: string): void => {
          const resource = bundle?.entry?.[0]?.resource;
          if (!resource) return;

          const term = resource.term?.[0];
          newCancelledExtensions.push({
            name,
            startDate: term?.applies?.start || null,
            endDate: term?.applies?.end || null,
          });
        };

        addCancelledExtension(
          cancelledDiscussionCase,
          "Agente de discussão de casos",
        );
        addCancelledExtension(
          cancelledMedicalReports,
          "Agente de relatórios médicos",
        );
        addCancelledExtension(
          cancelledGuidance,
          "Agente de orientações aos pacientes",
        );
        addCancelledExtension(
          cancelledPostAttendance,
          "Gestão de Pós-Atendimento",
        );

        setCancelledExtensions(newCancelledExtensions);
      } catch (error) {
        console.error(
          "Erro ao buscar contratos cancelados das extensões:",
          error,
        );
        setCancelledExtensions([]);
      } finally {
        setIsLoadingCancelledExtensions(false);
      }
    }

    if (isOpen) {
      fetchCancelledExtensions();
    } else {
      setCancelledExtensions([]);
    }
  }, [isOpen, organization?.id]);

  // Efeito para buscar os dados quando o diálogo for aberto
  useEffect(() => {
    if (isOpen && !hasFetchedRef.current && activeExtensions.length > 0) {
      fetchPaymentNoticeFluxPay();
      hasFetchedRef.current = true;
    }

    if (!isOpen) {
      hasFetchedRef.current = false;
    }
  }, [isOpen, activeExtensions.length, fetchPaymentNoticeFluxPay]);

  const handleNavigateToExtensions = () => {
    handleClose();
    navigate("/extensions");
  };

  return (
    <CommonDialog isOpen={isOpen} handleClose={handleClose} maxWidth="md">
      <DialogTitle align="center">Fluxmed Extensions</DialogTitle>
      <Grid container p={2} mb={2} spacing={2}>
        {/* Seção de extensões ativas */}
        <Grid item xs={12}>
          {activeExtensions.length > 0 ? (
            <>
              <Typography
                textAlign="center"
                variant="subtitle1"
                fontWeight={600}
                color="primary.main"
                mb={2}
              >
                Visão Geral das Extensões
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {activeExtensions.map((extension, index) => (
                    <Grid item xs={12} md={6} key={`extension-${index}`}>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "neutral300.main",
                          borderRadius: "8px",
                          padding: 2,
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                        }}
                      >
                        <Box sx={{ position: "absolute", top: 10, right: 10 }}>
                          <Chip
                            label="Ativo"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>

                        <Typography
                          variant="h6"
                          fontWeight={600}
                          color="primary.main"
                          mb={1}
                        >
                          {extension.name}
                        </Typography>

                        <Box sx={{ mt: "auto" }}>
                          <Divider sx={{ mb: 1.5 }} />
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="neutral700.main"
                          >
                            Data de ativação:
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            color="primary.main"
                          >
                            {extension.startDate
                              ? dayjs(extension.startDate).format("DD/MM/YYYY")
                              : "Data não disponível"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Seção de histórico de pagamentos do FluxPay */}
              {contractFluxPay && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Typography
                    textAlign="center"
                    variant="subtitle1"
                    fontWeight={600}
                    color="primary.main"
                    mb={2}
                  >
                    Histórico de Pagamentos - FluxPay
                  </Typography>

                  {isLoadingPaymentNoticeFluxPay ? (
                    <Grid item xs={12}>
                      <Skeleton width="100%" height="200px" />
                    </Grid>
                  ) : paymentNoticeFluxPay &&
                    paymentNoticeFluxPay.length > 0 ? (
                    <Grid item xs={12}>
                      <Stack
                        sx={{
                          border: "1px solid",
                          borderColor: "neutral300.main",
                          borderRadius: "8px",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                          overflow: "hidden",
                        }}
                      >
                        <GenericTable
                          tableColumns={[
                            { column: "Período", rowName: "paymentDate" },
                            {
                              column: "Valor pago à FluxMed",
                              rowName: "fluxmedAmount",
                            },
                            {
                              column: "Quantidade de consultas",
                              rowName: "consultCount",
                            },
                          ]}
                          tableRows={(() => {
                            const today = dayjs();
                            const currentDay = today.date();

                            // Determina o início do período atual
                            let currentPeriodStart;
                            if (currentDay >= 20) {
                              // Se estamos após dia 20, o período atual começa no dia 20 deste mês
                              currentPeriodStart = today.date(20);
                            } else {
                              // Se estamos antes do dia 20, o período atual começa no dia 20 do mês anterior
                              currentPeriodStart = today
                                .subtract(1, "month")
                                .date(20);
                            }

                            // Agrupa os pagamentos por período
                            const groupedPayments = paymentNoticeFluxPay.reduce(
                              (acc: IPeriod[], payment: IPaymentNotice) => {
                                const paymentDate = dayjs(
                                  payment?.resource?.paymentDate,
                                );

                                // Encontra ou cria o período apropriado
                                let period = acc.find((p) => {
                                  const startDate = dayjs(p.startDate);
                                  const endDate = dayjs(p.endDate);
                                  // Inclui as datas limites no período
                                  return (
                                    (paymentDate.isAfter(startDate, "day") ||
                                      paymentDate.isSame(startDate, "day")) &&
                                    (paymentDate.isBefore(endDate, "day") ||
                                      paymentDate.isSame(endDate, "day"))
                                  );
                                });

                                if (!period) {
                                  // Calcula o início e fim do período baseado na data do pagamento
                                  const periodStart =
                                    paymentDate.date() >= 20
                                      ? paymentDate.date(20)
                                      : paymentDate
                                          .subtract(1, "month")
                                          .date(20);

                                  // Se for o período atual, usa a data atual como fim
                                  const isCurrentPeriod = periodStart.isSame(
                                    currentPeriodStart,
                                    "day",
                                  );
                                  const periodEnd = isCurrentPeriod
                                    ? today
                                    : periodStart.add(1, "month").date(19);

                                  period = {
                                    startDate: periodStart.toDate(),
                                    endDate: periodEnd.toDate(),
                                    fluxmedAmount: 0,
                                    count: 0,
                                  };
                                  acc.push(period);
                                }

                                // Atualiza os valores do período
                                period.fluxmedAmount += 3.5; // Valor fixo por payment notice
                                period.count += 1;

                                return acc;
                              },
                              [],
                            );

                            // Ordena os períodos (mais recente primeiro)
                            return groupedPayments
                              .sort((a: IPeriod, b: IPeriod) =>
                                dayjs(b.startDate).diff(dayjs(a.startDate)),
                              )
                              .map((period: IPeriod) => ({
                                paymentDate: `${dayjs(period.startDate).format(
                                  "DD/MM/YYYY",
                                )} - ${dayjs(period.endDate).format(
                                  "DD/MM/YYYY",
                                )}`,
                                fluxmedAmount: useFormatter.formatCurrency(
                                  period.fluxmedAmount * 100,
                                ),
                                consultCount: period.count,
                              }));
                          })()}
                        />
                      </Stack>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 4,
                          px: 2,
                          backgroundColor: "neutral50.main",
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: "neutral300.main",
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="neutral700.main"
                          align="center"
                        >
                          Nenhum pagamento encontrado no período
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                px: 2,
                backgroundColor: "neutral50.main",
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "neutral300.main",
              }}
            >
              <Typography
                variant="h6"
                color="neutral700.main"
                align="center"
                gutterBottom
              >
                Nenhuma extensão ativa no momento
              </Typography>
              <Typography
                variant="body1"
                color="neutral600.main"
                align="center"
                mb={3}
              >
                Ative extensões para melhorar sua experiência com o FluxMed
              </Typography>
              <PrimaryButton
                width="220px"
                height="50px"
                onClick={handleNavigateToExtensions}
              >
                Visualizar extensões
              </PrimaryButton>
            </Box>
          )}
        </Grid>

        {/* Accordion com contratos cancelados, sempre no final do diálogo */}
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          <Typography
            sx={{ color: "primary.main", width: "100%" }}
            variant="subtitle1"
            textAlign="center"
            fontWeight={600}
          >
            Contratos de extensões cancelados
          </Typography>

          {isLoadingCancelledExtensions ? (
            <Skeleton width="100%" height="120px" />
          ) : cancelledExtensions.length > 0 ? (
            <Stack
              sx={{
                border: "1px solid",
                borderColor: "neutral300.main",
                borderRadius: "8px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                overflow: "hidden",
              }}
            >
              <GenericTable
                tableColumns={[
                  { column: "Extensão", rowName: "name" },
                  { column: "Início", rowName: "startDate" },
                  { column: "Término", rowName: "endDate" },
                ]}
                tableRows={cancelledExtensions.map((extension) => ({
                  name: extension.name,
                  startDate: extension.startDate
                    ? dayjs(extension.startDate).format("DD/MM/YYYY")
                    : "Data não disponível",
                  endDate: extension.endDate
                    ? dayjs(extension.endDate).format("DD/MM/YYYY")
                    : "Sem data de término",
                }))}
              />
            </Stack>
          ) : (
            <NoDataWarning message="Nenhum contrato de extensão cancelado encontrado." />
          )}
        </Grid>
      </Grid>
    </CommonDialog>
  );
}
